import React, { useState, useEffect, useRef } from 'react';
import {
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    ListItemIcon,
    IconButton,
    Box,
    Typography,
    Tooltip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import ImageIcon from '@mui/icons-material/Image';
import QrCodeIcon from '@mui/icons-material/QrCode';
import DynamicFormIcon from '@mui/icons-material/DynamicForm';
import RemoveIcon from '@mui/icons-material/Remove';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DesignElement } from '../../../types/coupon';

export interface LayersPanelProps {
    elements: DesignElement[];
    selectedElementId: string | null;
    onElementSelect: (id: string | null) => void;
    onElementUpdate: (element: DesignElement) => void;
    onElementsUpdate: (elements: DesignElement[]) => void;
}

interface SortableItemProps {
    element: DesignElement;
    selectedElementId: string | null;
    onElementSelect: (id: string | null) => void;
    onElementUpdate: (element: DesignElement) => void;
    getElementIcon: (type: DesignElement['type']) => React.ReactNode;
    getElementLabel: (element: DesignElement) => string;
}

const SortableItem = ({
    element,
    selectedElementId,
    onElementSelect,
    onElementUpdate,
    getElementIcon,
    getElementLabel
}: SortableItemProps) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(element.name || getElementLabel(element));
    const inputRef = useRef<HTMLInputElement>(null);

    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: element.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    const handleVisibilityToggle = () => {
        onElementUpdate({
            ...element,
            style: {
                ...element.style,
                visibility: element.style?.visibility === 'hidden' ? 'visible' : 'hidden'
            }
        });
    };

    const handleLockToggle = () => {
        onElementUpdate({
            ...element,
            style: {
                ...element.style,
                pointerEvents: element.style?.pointerEvents === 'none' ? 'auto' : 'none'
            }
        });
    };

    const handleDoubleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsEditing(true);
        setEditValue(element.name || getElementLabel(element));
    };

    const handleEditComplete = () => {
        if (isEditing) {
            const newName = editValue.trim();
            if (newName && newName !== element.name) {
                onElementUpdate({
                    ...element,
                    name: newName
                });
            }
            setIsEditing(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleEditComplete();
        } else if (e.key === 'Escape') {
            setIsEditing(false);
            setEditValue(element.name || getElementLabel(element));
        }
    };

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [isEditing]);

    return (
        <ListItem
            ref={setNodeRef}
            style={style}
            selected={selectedElementId === element.id}
            onClick={() => onElementSelect(element.id)}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                pr: 8,
                cursor: 'pointer',
                userSelect: 'none',
                '&:hover': {
                    bgcolor: 'action.hover'
                }
            }}
        >
            <Box {...attributes} {...listeners} sx={{ 
                cursor: 'grab',
                display: 'flex',
                alignItems: 'center',
                color: 'text.secondary',
                '&:hover': { color: 'primary.main' },
                '& .MuiSvgIcon-root': {
                    fontSize: '1rem'
                }
            }}>
                <DragIndicatorIcon />
            </Box>
            <ListItemIcon sx={{ minWidth: 32 }}>
                {getElementIcon(element.type)}
            </ListItemIcon>
            {isEditing ? (
                <Box 
                    onClick={(e) => e.stopPropagation()} 
                    sx={{ 
                        flex: 1,
                        mr: 2
                    }}
                >
                    <input
                        type="text"
                        ref={inputRef}
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={handleEditComplete}
                        onKeyDown={handleKeyDown}
                        style={{
                            width: '100%',
                            padding: '4px 8px',
                            fontSize: '0.875rem',
                            border: '1px solid rgba(0, 0, 0, 0.23)',
                            borderRadius: '4px',
                            backgroundColor: 'transparent'
                        }}
                    />
                </Box>
            ) : (
                <ListItemText 
                    primary={element.name || getElementLabel(element)}
                    onDoubleClick={handleDoubleClick}
                    sx={{ 
                        m: 0,
                        '& .MuiTypography-root': {
                            fontSize: '0.875rem'
                        }
                    }}
                />
            )}
            <ListItemSecondaryAction sx={{
                right: 8,
                display: 'flex',
                gap: 0.5
            }}>
                <Tooltip title={element.style?.visibility === 'hidden' ? 'Show' : 'Hide'}>
                    <IconButton
                        onClick={handleVisibilityToggle}
                        size="small"
                        sx={{ p: 0.5 }}
                    >
                        {element.style?.visibility === 'hidden' ? (
                            <VisibilityOffIcon sx={{ fontSize: '1rem' }} />
                        ) : (
                            <VisibilityIcon sx={{ fontSize: '1rem' }} />
                        )}
                    </IconButton>
                </Tooltip>
                <Tooltip title={element.style?.pointerEvents === 'none' ? 'Unlock' : 'Lock'}>
                    <IconButton
                        onClick={handleLockToggle}
                        size="small"
                        sx={{ p: 0.5 }}
                    >
                        {element.style?.pointerEvents === 'none' ? (
                            <LockIcon sx={{ fontSize: '1rem' }} />
                        ) : (
                            <LockOpenIcon sx={{ fontSize: '1rem' }} />
                        )}
                    </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                    <IconButton
                        onClick={() => {
                            if (selectedElementId === element.id) {
                                onElementSelect(null);
                            }
                            onElementUpdate({
                                ...element,
                                style: {
                                    ...element.style,
                                    visibility: 'hidden'
                                }
                            });
                        }}
                        size="small"
                        sx={{ p: 0.5 }}
                    >
                        <DeleteIcon sx={{ fontSize: '1rem' }} />
                    </IconButton>
                </Tooltip>
            </ListItemSecondaryAction>
        </ListItem>
    );
};

export const LayersPanel: React.FC<LayersPanelProps> = ({
    elements,
    selectedElementId,
    onElementSelect,
    onElementUpdate,
    onElementsUpdate
}) => {
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const getElementIcon = (type: DesignElement['type']) => {
        const iconProps = { fontSize: 'small' as const, sx: { fontSize: '1.1rem' } };
        switch (type) {
            case 'text':
                return <TextFieldsIcon {...iconProps} />;
            case 'image':
                return <ImageIcon {...iconProps} />;
            case 'qrcode':
                return <QrCodeIcon {...iconProps} />;
            case 'dynamic':
                return <DynamicFormIcon {...iconProps} />;
            case 'divider':
                return <RemoveIcon {...iconProps} />;
            default:
                return null;
        }
    };

    const getElementLabel = (element: DesignElement): string => {
        return element.name || getDefaultLabel(element);
    };

    const getDefaultLabel = (element: DesignElement): string => {
        switch (element.type) {
            case 'text':
                return `${element.content.substring(0, 20)}${element.content.length > 20 ? '...' : ''}`;
            case 'image':
                return 'Image';
            case 'qrcode':
                return 'QR Code';
            case 'dynamic':
                return `${element.dynamicField || 'Unnamed'}`;
            case 'divider':
                return 'Divider';
            default:
                return 'Element';
        }
    };

    const handleDragEnd = (event: any) => {
        const { active, over } = event;
        
        if (active.id !== over.id) {
            const oldIndex = elements.findIndex(e => e.id === active.id);
            const newIndex = elements.findIndex(e => e.id === over.id);
            
            const newElements = [...elements];
            const [movedElement] = newElements.splice(oldIndex, 1);
            newElements.splice(newIndex, 0, movedElement);

            // Update z-index for all elements
            const updatedElements = newElements.map((el, idx) => ({
                ...el,
                style: {
                    ...el.style,
                    zIndex: newElements.length - idx // Higher index = appears on top
                }
            }));

            // Update all elements at once
            onElementsUpdate(updatedElements);
        }
    };

    return (
        <Box sx={{ 
            width: '100%', 
            maxWidth: 360, 
            bgcolor: 'background.paper', 
            display: 'flex', 
            flexDirection: 'column', 
            height: '100%' 
        }}>
            <Typography variant="h6" sx={{ p: 2, pb: 1 }}>
                Layers
            </Typography>
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
                modifiers={[restrictToVerticalAxis]}
            >
                <SortableContext
                    items={elements.map(e => e.id)}
                    strategy={verticalListSortingStrategy}
                >
                    <List dense sx={{ flex: 1, overflowY: 'auto' }}>
                        {elements.map((element) => (
                            <SortableItem
                                key={element.id}
                                element={element}
                                selectedElementId={selectedElementId}
                                onElementSelect={onElementSelect}
                                onElementUpdate={onElementUpdate}
                                getElementIcon={getElementIcon}
                                getElementLabel={getElementLabel}
                            />
                        ))}
                        {elements.length === 0 && (
                            <ListItem>
                                <ListItemText
                                    primary="No elements"
                                    sx={{ textAlign: 'center', color: 'text.secondary' }}
                                />
                            </ListItem>
                        )}
                    </List>
                </SortableContext>
            </DndContext>
        </Box>
    );
};

export default LayersPanel;
